import { Injectable } from '@angular/core';
import { Subject } from '../models/subject';
import { SubjectTypeService } from './subject-type.service';
import { PrintHtmlService } from './print-html.service';
import { forkJoin, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ProcessService } from './process.service';
import { Process } from '../models/process';
import { INote } from '../interfaces/entity';
import { PrintService } from './print.service';
import { TaskService } from './task.service';
import { Task } from '../models/task';
import { AttachmentService, enumAttachmentType } from './attachment.service';
import { SubjectLayoutService } from './subject-layout.service';
import { NoteService } from './note.service';
import { SubjectService } from './subject.service';

@Injectable()
export class SubjectPrintService {

  constructor(
    private subjectTypeService: SubjectTypeService,
    private printService: PrintService,
    private printHtmlService: PrintHtmlService,
    private processService: ProcessService,
    private noteService: NoteService,
    private taskService: TaskService,
    private attachmentService: AttachmentService,
    private subjectService: SubjectService,
    private subjectLayoutService: SubjectLayoutService,
  ) { }

  print(subject: Subject, options?: any) {
    const subjectTypeName = this.subjectTypeService.getName(subject.subjectTypeId);
    const isReferral = subject.isReferral;
    const parent = { id: subject.id, uid: subject.uid, type: isReferral ? 'referral' : 'subject', summary: subject.summary, subjectType: subjectTypeName };
    const title = (isReferral ? 'Referral ' : '') + ((subjectTypeName) ? subjectTypeName + ' Print' : 'Print') + ((parent.summary) ? ` for ${parent.summary}` : '');
    let html = '';
    const print$ = [];

    // Header
    print$.push(of(this.printHtmlService.getHeader(title) + this.printHtmlService.sectionBreak));

    // Subject card
    if (options.subject) {
      print$.push(this.subjectLayoutService.getSubjectCardWidgets(subject).pipe(take(1), map((response: any) => {
        return this.printHtmlService.getSubjectWidgets(parent, response.widgets, response.x);
      })));
    }

    // Linked Subjects (Type, relationship, summary)
    if (options.linkedSubjects) {
      const linkedSubjects = [];
      subject.subjectLinks.forEach((item) => {
        // check if has a linked subject and not already added to array of linked subjects with the same relationship
        if (item.linkedSubject && (linkedSubjects.findIndex((x) => ((x.id === item.linkedSubject.id) && (x.relationshipId === item.relationship.id))) < 0)) {
          const linkedSubject = new Subject(item.linkedSubject);
          linkedSubjects.push({
            type: this.subjectTypeService.getName(linkedSubject.subjectTypeId),
            relationship: item.relationship.name,
            summary: linkedSubject.summary,
          });
        }
      });

      // ...at this point we have a list of linked subjects
      // add section break and header
      print$.push(of(this.printHtmlService.getLinkedSubjects(parent, linkedSubjects)));
    }

    // Assessments
    if (options.assessments) {
      print$.push(of(this.printHtmlService.getAssessments(parent, subject.subjectAssessments.map((i) => i.assessment))));
    }

    // Linked Markers
    if (options.linkedSubjectMarkers) {
      subject.linkedSubjectMarkers.sort((a, b) => (b.subjectId - a.subjectId) || a.marker.name.localeCompare(b.marker.name));
      print$.push(of(this.printHtmlService.getLinkedMarkers(parent, subject.linkedSubjectMarkers)));
    }

    // Markers
    if (options.markers) {
      subject.subjectMarkers = (this.subjectService.markerSortAscending) ?
        subject.subjectMarkers.sort((a, b) => a.marker.name.localeCompare(b.marker.name)) : subject.subjectMarkers.sort((a, b) => b.marker.name.localeCompare(a.marker.name));
      print$.push(of(this.printHtmlService.getMarkers(parent, subject.subjectMarkers.map((i) => i.marker))));
    }

    // Referral(s)
    if (options.referrals) {
      print$.push(of(this.printHtmlService.getReferrals(parent, subject.subjectReferrals.map((i) => i.referral))));
    }

    // Reviews
    if (options.reviews) {
      print$.push(of(this.printHtmlService.getReviews(parent, subject.subjectReviews.map((i) => i.review))));
    }

    // Processes - options.processes should contain an array of process type names to include
    if (options.processes && (options.processes.length > 0)) {      
      options.processes.forEach((i) => {        
        print$.push(this.processService.getSubjectProcesses(subject, i).pipe(take(1), map((resp: Process[]) => {          
          const processes = resp;
          return this.printHtmlService.getProcesses(parent, i, processes);
        })));
      });
    }

    // Tasks
    if (options.tasks) {
      const params = 'subjectUid=' + subject.uid;
      print$.push(this.taskService.getAllData(null, params).pipe(take(1), map((resp: Task[]) => {
        const tasks = this.taskService.applySortOrder(resp, options.tasksSortOrder);
        return this.printHtmlService.getTasks(parent, tasks);
      })));
    }

    // List of Attachments
    if (options.listAttachments) {
      print$.push(this.attachmentService.getExistingAttachments(parent, true, true).pipe(take(1), map((attachments) => {
        attachments = attachments || [];
        attachments = attachments.filter((i) => !i.isDirectory);
        if (options.attachmentLabels) {
          const requiredLabels = options.attachmentLabels.toLowerCase().replace(/\s/g, '').split(',');
          attachments = attachments.filter((i) => {
            let labels = ((i.attachmentType === enumAttachmentType.Url) && i.url) ? i.url.labels : i.labels;
            if (labels) {
              labels = labels.toLowerCase().replace(/\s/g, '').split(',');
              if (labels.some((x) => requiredLabels.indexOf(x) > -1)) {
                return i;
              }
            }
          });
        }
        return this.printHtmlService.getAttachments(parent, attachments);
      })));
    }

    // Notes - Drafts, Starred, Important, Sensitive
    if (options.notes) {
      print$.push(this.noteService.getNotesForParent(parent).pipe(map((resp: INote[]) => {
        let notes = this.noteService.sortNotes(resp, options.noteOrderAscending);

        // if not include drafts then remove them
        if (!options.notes.drafts) {
          notes = notes.filter((i) => i.sequenceId);
        }
        // if only starred then remove notes that are not starred
        if (options.notes.starred) {
          notes = notes.filter((i) => i.note.starred);
        }
        // if only important then remove notes that are not important
        if (options.notes.important) {
          notes = notes.filter((i) => i.note.important);
        }
        // if not include sensitive then remove them
        if (!options.notes.sensitive) {
          notes = notes.filter((i) => !i.note.sensitive);
        }
        return this.printHtmlService.getNotes(parent, notes);
      })));
    }

    // Footer
    print$.push(of(this.printHtmlService.sectionBreak + this.printHtmlService.printedBy(true)));

    if (print$.length > 0) {
      forkJoin(print$).subscribe((response) => {
        response.forEach((i) => {
          html = html + i;
        });
        this.printService.printHtml(title, html, this.printHtmlService.cardStyle, false);
      });
    }
  }
}
